import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { AUTH_ENDPOINTS } from "../../../config/constants";

// Function to get select styles based on dark mode
const getSelectStyles = () => {
  const isDark = document.documentElement.classList.contains("dark");

  return {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : isDark ? "#4b5563" : "#e5e7eb",
      backgroundColor: isDark
        ? state.isFocused
          ? "#374151"
          : "#1f2937"
        : state.isFocused
        ? "#f9fafb"
        : "white",
      color: isDark ? "#e5e7eb" : "#374151",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#3b82f6",
      },
      minHeight: "38px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? isDark
          ? "#374151"
          : "#bfdbfe"
        : isDark
        ? "#1f2937"
        : "white",
      color: state.isSelected ? "white" : isDark ? "#e5e7eb" : "#374151",
      "&:active": {
        backgroundColor: "#3b82f6",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#1f2937" : "white",
      border: isDark ? "1px solid #4b5563" : "1px solid #e5e7eb",
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: isDark ? "#1f2937" : "white",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? "#e5e7eb" : "#374151",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? "#9ca3af" : "#6b7280",
    }),
    input: (base) => ({
      ...base,
      color: isDark ? "#e5e7eb" : "#374151",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: isDark ? "#4b5563" : "#e5e7eb",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: isDark ? "#9ca3af" : "#6b7280",
      "&:hover": {
        color: isDark ? "#e5e7eb" : "#374151",
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: isDark ? "#9ca3af" : "#6b7280",
      "&:hover": {
        color: isDark ? "#e5e7eb" : "#374151",
      },
    }),
  };
};

const BasicInfo = ({
  user,
  make,
  setMake,
  model,
  setModel,
  color,
  setColor,
  bodyStyle,
  setBodyStyle,
  g80ConversionKit,
  setG80ConversionKit,
  makes,
  loadingMakes,
  errorMakes,
  makeOptions,
  modelOptions,
  colorOptions,
  bodyStyleOptions,
  g80Options,
  basicInfoMessage,
  onSave,
}) => {
  const { t } = useTranslation();
  const [hasChanges, setHasChanges] = useState(false);
  const [themeKey, setThemeKey] = useState(0); // Force re-render on theme change
  const [initialValues, setInitialValues] = useState({
    make,
    model,
    color,
    bodyStyle,
    g80ConversionKit,
  });

  useEffect(() => {
    // Update initial values when vehicle data changes
    setInitialValues({
      make,
      model,
      color,
      bodyStyle,
      g80ConversionKit,
    });
  }, []); // Empty dependency array means this only runs once on mount

  // Listen for theme changes to update select styles
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          setThemeKey((prev) => prev + 1); // Force re-render
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const currentValues = {
      make,
      model,
      color,
      bodyStyle,
      g80ConversionKit,
    };

    // Check if any value is different from initial
    const changed = Object.entries(currentValues).some(
      ([key, value]) => value !== initialValues[key]
    );

    setHasChanges(changed);
  }, [make, model, color, bodyStyle, g80ConversionKit, initialValues]);

  const handleSave = () => {
    onSave();
    // Update initial values after save
    setInitialValues({
      make,
      model,
      color,
      bodyStyle,
      g80ConversionKit,
    });
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-3 dark:text-gray-400">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold">
            {t("vehicleProfile.basicInfo.title")}
          </h3>
          <div className="relative group">
            <svg
              className="w-4 h-4 text-gray-400 cursor-help"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 dark:bg-gray-700">
              {t("vehicleProfile.basicInfo.tooltip")}{" "}
              <a
                href="https://discord.gg/kDN8mkaKAG"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
                onClick={(e) => e.stopPropagation()}
              >
                {t("vehicleProfile.basicInfo.joinDiscord")}
              </a>{" "}
              {t("vehicleProfile.basicInfo.toSuggest")}
              <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-gray-800 transform rotate-45"></div>
            </div>
          </div>
        </div>
        {!user && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <a
              href={`${
                AUTH_ENDPOINTS.GOOGLE_LOGIN
              }?returnTo=${encodeURIComponent(
                window.location.pathname + window.location.search
              )}`}
              className="text-blue-500 hover:text-blue-600 transition-colors"
              onClick={(e) => {
                const returnPath =
                  window.location.pathname + window.location.search;
                sessionStorage.setItem("preLoginPath", returnPath);
                localStorage.setItem("preLoginPath", returnPath);
              }}
            >
              {t("vehicleProfile.basicInfo.loginToEdit")}
            </a>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-400 ">
              {t("vehicleProfile.fields.make")}
            </label>
            <Select
              key={`make-${themeKey}`}
              value={makeOptions.find((option) => option.value === make)}
              onChange={(option) => {
                setMake(option?.value || "");
                setModel(""); // Reset model when make changes
              }}
              options={makeOptions}
              isDisabled={!user || loadingMakes}
              styles={getSelectStyles()}
              placeholder={
                loadingMakes
                  ? t("vehicleProfile.basicInfo.loadingMakes")
                  : t("vehicleProfile.basicInfo.selectMake")
              }
              isClearable
              isLoading={loadingMakes}
            />
            {errorMakes && (
              <div className="text-xs text-red-600 mt-1">{errorMakes}</div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-400">
              {t("vehicleProfile.fields.model")}
            </label>
            <Select
              key={`model-${themeKey}`}
              value={modelOptions.find((option) => option.value === model)}
              onChange={(option) => setModel(option?.value || "")}
              options={modelOptions}
              isDisabled={!user || !make || loadingMakes}
              styles={getSelectStyles()}
              placeholder={
                make
                  ? t("vehicleProfile.basicInfo.selectModel")
                  : t("vehicleProfile.basicInfo.selectMakeFirst")
              }
              isClearable
              isLoading={loadingMakes}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-400">
              {t("vehicleProfile.fields.color")}
            </label>
            <Select
              key={`color-${themeKey}`}
              value={
                color
                  ? colorOptions.find((option) => option.value === color)
                  : null
              }
              onChange={(option) => setColor(option?.value || "")}
              options={colorOptions}
              isDisabled={!user}
              styles={getSelectStyles()}
              placeholder={t("vehicleProfile.basicInfo.selectColor")}
              isClearable
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-400">
              {t("vehicleProfile.fields.bodyStyle")}
            </label>
            <Select
              key={`bodyStyle-${themeKey}`}
              value={
                bodyStyle
                  ? bodyStyleOptions.find(
                      (option) => option.value === bodyStyle
                    )
                  : null
              }
              onChange={(option) => setBodyStyle(option?.value || "")}
              options={bodyStyleOptions}
              isDisabled={!user}
              styles={getSelectStyles()}
              placeholder={t("vehicleProfile.basicInfo.selectBodyStyle")}
              isClearable
            />
          </div>
        </div>

        <div className="space-y-3">
          {make === "BMW" && model !== "M3" && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-400">
                {t("vehicleProfile.basicInfo.hasG80Kit")}
              </label>
              <Select
                key={`g80Kit-${themeKey}`}
                value={g80Options.find(
                  (option) => option.value === g80ConversionKit
                )}
                onChange={(option) =>
                  setG80ConversionKit(option?.value || "No")
                }
                options={g80Options}
                isDisabled={!user}
                styles={getSelectStyles()}
                placeholder={t("vehicleProfile.basicInfo.selectOption")}
              />
            </div>
          )}
        </div>
      </div>

      {basicInfoMessage && (
        <div
          className={`mt-3 p-2 rounded-md text-sm dark:text-gray-400 ${
            basicInfoMessage.type === "error"
              ? "bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-100"
              : "bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100"
          }`}
        >
          {basicInfoMessage.text}
        </div>
      )}

      {user && hasChanges && (
        <div className="mt-4 flex justify-end">
          <button
            className="px-4 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-md hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 text-sm dark:from-primary-600 dark:to-primary-700 dark:hover:from-primary-700 dark:hover:to-primary-800 dark:active:from-primary-800 dark:active:to-primary-900"
            onClick={handleSave}
          >
            {t("vehicleProfile.basicInfo.saveChanges")}
          </button>
        </div>
      )}
    </div>
  );
};

export default BasicInfo;
